"use client";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef, useState } from "react";

// components

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { motion } from "framer-motion";
import Link from "next/link";
import { FiLogIn } from "react-icons/fi";

// data
import { baseUrl } from "@/utils/helpers/createUrl";
import { signIn } from "@/utils/serverActions/authActions";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";

// icons
import { useSession } from "@/context/SessionContext";
import { clearAuthCookies } from "@/utils/cookie-utils";
import { createClient } from "@/utils/supabase/client";
import { isValidEmail } from "@/utils/sanitize/email";

const supabase = createClient();

function SignInContent() {
  const [error, setError] = useState("");
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {} = useSession();

  const searchParams = useSearchParams();
  const initialEmail = searchParams.get("email") || "";

  // Clear any existing cookies on component mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("SignIn page loaded, clearing any existing auth cookies");
      clearAuthCookies();

      // Clear any client-side storage as well
      localStorage.clear();
      sessionStorage.clear();
    }
  }, []);

  // Add timeout protection for loading states
  useEffect(() => {
    if (isLoading) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        console.warn("SignIn loading timeout - forcing reset");
        setIsLoading(false);
        setError("La solicitud tardó demasiado. Por favor, intenta de nuevo.");
      }, 30000); // 30 second timeout

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [isLoading]);

  const handleResendVerification = async (email: string) => {
    try {
      setError("");

      // Add timeout for resend verification
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
      });

      clearTimeout(timeoutId);

      if (error) throw error;
      setError(
        "Correo de verificación reenviado. Revisa tu bandeja de entrada.",
      );
      setShowResendVerification(false);
    } catch (err) {
      console.error("Resend verification error:", err);
      if (err instanceof Error && err.name === "AbortError") {
        setError("La solicitud tardó demasiado. Por favor, intenta de nuevo.");
      } else {
        setError(
          "Error al reenviar el correo de verificación. Por favor, intenta de nuevo.",
        );
      }
    } finally {
      // Reset any loading states if needed
    }
  };

  // Google sign-in handler (currently disabled in UI but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError("");

      // Add timeout for Google sign in
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/callback`,
        },
      });

      clearTimeout(timeoutId);

      if (error) throw error;
      // The user will be redirected to Google's login page
    } catch (err) {
      console.error("Google sign in error:", err);
      if (err instanceof Error && err.name === "AbortError") {
        setError("La solicitud tardó demasiado. Por favor, intenta de nuevo.");
      } else {
        setError(
          "Error al iniciar sesión con Google. Por favor, intenta de nuevo.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  type formikValuesType = {
    email: string;
    password: string;
  };

  const handleSubmit = async (
    values: formikValuesType,
    actions: FormikHelpers<formikValuesType>,
  ) => {
    let hasError = false;

    try {
      setIsLoading(true);
      actions.setSubmitting(true);
      setError("");

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Add timeout for sign in request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);

      const response = await signIn(values);

      clearTimeout(timeoutId);

      if (!response.ok) {
        hasError = true;
        setShowResendVerification(false);

        const errorMessage = response.hint
          ? `${response.message} ${response.hint}`
          : response.message;

        if (response.code === "email_not_confirmed") {
          setError(errorMessage);
          setShowResendVerification(true);
        } else {
          setError(errorMessage || "Error de autenticacion");
        }

        if (response.code || errorMessage) {
          return;
        }

        switch (response.code) {
          case "email_not_confirmed":
            setError(errorMessage);
            setShowResendVerification(true);
            break;
          case "invalid_credentials":
            setError("Credenciales inválidas");
            break;
          case "User profile not fully set up. Please contact administrator.":
            setError(
              "Perfil de usuario no configurado completamente. Contacta al administrador.",
            );
            break;
          default:
            setError(response.error || "Error de autenticación");
        }
        // Reset retry count on error
      } else {
        console.log("Signin successful, determining next route");
        console.log("Response data:", response);

        const nextRoute = response.nextRoute || "/scheduling";
        console.log("Next route determined from server:", nextRoute);

        if (
          response.subdomain &&
          nextRoute !== "/invitations" &&
          nextRoute !== "/create-company"
        ) {
          // For subdomain users, redirect to subdomain version of the route
          const subdomainUrl = `${baseUrl(
            response.subdomain as string,
          )}${nextRoute}`;
          window.location.replace(subdomainUrl);
        } else {
          // For regular users or special routes, use the determined route
          window.location.replace(nextRoute);
        }

        // Don't reset loading state here - let the redirect handle it
        return;
      }
    } catch (err) {
      hasError = true;
      console.error("Sign in error:", err);

      if (err instanceof Error && err.name === "AbortError") {
        setError("La solicitud tardó demasiado. Por favor, intenta de nuevo.");
      } else if (
        err instanceof Error &&
        err.message.includes("unexpected response")
      ) {
        // Next throws this when the server action gets something that is not an
        // action response, e.g. the firewall's 429 (too many requests from this IP).
        setError(
          "El servidor rechazó la solicitud, probablemente por demasiados intentos. Espera un minuto e intenta de nuevo.",
        );
      } else {
        setError("Ocurrió un error inesperado. Por favor, intenta de nuevo.");
      }
    } finally {
      // Only reset loading state if there was an error
      // For successful sign-ins, keep loading state active during redirect
      if (hasError) {
        setIsLoading(false);
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <div className="flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full shadow-md p-8 max-h-[90dvh] overflow-y-auto"
      >
        <div className="flex flex-col items-center mb-6">
          {/* <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-8"> */}

          <div className="rounded-full bg-ac-4 flex items-center justify-center">
            <FiLogIn size={24} className="text-ac m-3" />
          </div>
          {/* </div> */}
          <h3 className="pt-6 text-fg">Iniciar sesión</h3>
          <p className="text-fg-600 mt-2">
            Ingresa tus credenciales para acceder a tu cuenta
          </p>
        </div>

        <Formik
          // passes the auth provider,in this case it's storing
          // email and pass instead of using OAuth
          initialValues={{
            email: initialEmail,
            password: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={object().shape({
            email: string()
              .required("Email is required")
              // .email("Email is invalid")
              .test("email-shape", "Please enter a valid email address", (value) => isValidEmail(value ?? ""))
              .max(50, "Max 50 digits"),
            password: string().required("Password is required"),
          })}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            isSubmitting,
          }) => (
            <Form>
              <div className="space-y-4">
                <Input
                  id="email"
                  error={errors.email}
                  name="email"
                  onChange={handleChange("email")}
                  onBlur={handleBlur("email")}
                  placeholder="Email address"
                  touched={touched.email}
                  type="email"
                  value={values.email}
                  variant={
                    touched.email && errors.email ? "destructive" : "default"
                  }
                />
                <div>
                  <Input
                    id="password"
                    error={errors.password}
                    name="password"
                    onBlur={handleBlur("password")}
                    onChange={handleChange("password")}
                    placeholder="Password"
                    touched={touched.password}
                    type="password"
                    value={values.password}
                    variant={
                      touched.password && errors.password
                        ? "destructive"
                        : "default"
                    }
                  />

                  <div className="text-right">
                    <Link
                      href="/forgot-password"
                      className="text-sm text-ac hover:underline"
                    >
                      Olvidaste tu contraseña?
                    </Link>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full mt-6"
                  disabled={isSubmitting || isLoading}
                  isLoading={isSubmitting || isLoading}
                >
                  {isSubmitting || isLoading
                    ? "Iniciando sesión..."
                    : "Iniciar sesión"}
                </Button>
                {error && (
                  <div className="flex flex-col space-y-2 text-sm mt-2">
                    <div className="flex justify-between items-start">
                      <span className="text-de+2 mr-2 flex-1">{error}</span>
                    </div>
                    {showResendVerification && (
                      <button
                        onClick={() => handleResendVerification(values.email)}
                        className="text-ac hover:underline"
                      >
                        Reenviar correo de verificación
                      </button>
                    )}
                  </div>
                )}
              </div>
              {/* <div className="my-6 flex items-center">
                <div className="flex-grow border-t border-fg"></div>
                <p className="mx-4 text-fg">or</p>
                <div className="flex-grow border-t border-fg"></div>
              </div>

              <div className="space-y-4">
                <Button
                  variant="outline"
                  className="bg-secondary-to-white w-full flex items-center justify-center"
                  onClick={handleGoogleSignIn}
                >
                  <Image
                    src={GoogleIcon}
                    alt="Google"
                    width={15}
                    height={15}
                    className="mr-2"
                  />
                  Iniciar sesión con Google
                </Button>
                <Button
                  variant="outline"
                  className="bg-secondary-to-white w-full justify-start"
                >
                  <Image
                    src={AppleIcon}
                    alt="Apple"
                    width={15}
                    height={15}
                    className="mr-2"
                  />
                  Iniciar sesión con Apple
                </Button>
              </div> */}
            </Form>
          )}
        </Formik>
        <p className="mt-6 text-center text-sm text-fg">
          No tienes una cuenta?{" "}
          <Link href="/signup" className="text-ac hover:underline">
            Registrarse
          </Link>
        </p>
      </motion.div>
    </div>
  );
}

export default function SignIn() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-dvh">
          <div className="text-center">Cargando...</div>
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  );
}
